/* eslint @typescript-eslint/no-explicit-any: 0 */

import { Link } from 'react-router';

import { setNavigationContext } from '~/utils/navigationContext';
import { appendTrailingSlash } from '~/utils/trailingSlash';
import ProductCardInteractionEvent from '~/events/ProductCardInteractionEvent';
import SearchResultClickEvent from '~/events/SearchResultClickEvent';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import type { ProductSearchResultProps } from '~/legacy/types';
import { IMAGE_TYPES, RECORD_SUBTYPES } from '~/legacy/utils';
import type { ProductSubType } from 'types/capi/shared';

import GranularResponsiveImage from './GranularResponsiveImage';

const MODULE_TYPE_BY_SUB_TYPE: Record<ProductSubType, string> = {
  sneaker: 'search-panel-footwear',
  clothing: 'search-panel-clothing'
};

const getPath = ({
  canonical_path,
  slug
}: {
  canonical_path: string;
  slug: string;
}) =>
  appendTrailingSlash(canonical_path ? canonical_path : `/products/${slug}/`);

const ProductSearchResult = ({
  onClick,
  position,
  insightsFields = {},
  query,
  hit,
  hit: {
    name,
    objectID,
    price,
    tracking_id,
    variants,
    images: {
      featured: { alt: featuredAlt, original_img_url } = {
        alt: '',
        original_img_url: ''
      }
    } = {}
  },
  sub_type
}: ProductSearchResultProps) => {
  const image = original_img_url ? (
    <GranularResponsiveImage
      alt={featuredAlt || name}
      url={original_img_url}
      title={name}
      responsiveImageType={IMAGE_TYPES.SEARCH_RESULT}
      preserveRatio={sub_type === RECORD_SUBTYPES.CLOTHING}
    />
  ) : (
    ''
  );

  const link = {
    pathname: getPath(hit as any),
    state: { hit, insightsFields }
  };

  const { isBot } = useRootData();
  const user = useUser();

  const handleSearchResultClick = (e: React.MouseEvent<HTMLElement>) => {
    setNavigationContext({
      pagePosition: 'Header' as const,
      position,
      previousPageType: 'search'
    });

    new SearchResultClickEvent({
      isBot,
      product_name: name,
      search_term: query
    }).send();

    // The legacy index keys a product record by `product:<slug>`; the numeric product id every
    // other product_card carries is `tracking_id`, and stock lives on the variants, not the root.
    const [primaryVariant] = variants ?? [];

    new ProductCardInteractionEvent({
      gesture: 'click',
      guestId: user.guestId,
      isBot,
      moduleType: MODULE_TYPE_BY_SUB_TYPE[sub_type],
      position: position ?? 1,
      product: {
        id: tracking_id,
        price,
        stock_status: primaryVariant?.stock_status
      },
      searchTerm: query
    }).send();

    onClick(e);
  };

  return (
    <Link
      onClick={(e) => {
        handleSearchResultClick(e);
      }}
      to={link}
      className="search-panel__list-item"
      rel="product"
    >
      <div className="search-panel__image">{image}</div>
      <section className="search-panel__description">
        <h5
          className="font-heading text-sm font-normal line-clamp-3"
          id={`aria-product-title--${objectID}`}
        >
          {name}
        </h5>
      </section>
    </Link>
  );
};

export default ProductSearchResult;
